import { action, observable, makeObservable } from 'mobx';
import { persist } from 'mobx-persist';
import { ordersStore } from '../';
import { TOrder } from '../../../types/orders';
import { ORDER_STATUS } from 'src/constants/order';
import { USER_PERMISSIONS } from 'src/constants/user';
import player from 'src/services/audio';
import { TOrderCRUDEvents } from 'src/types/orders';
import { hasPermissions } from 'src/utils/helpers';
import { debounce } from 'src/utils/perfomance';
import logger from '~/services/logger';

/**
 * 10 hours = 36.000.000 milliseconds.
 * 3 second = 3.000 milliseconds.
 */
const ORDER_NOTIFICATION_TIMEOUT_MAP: {
  [key: string]: number;
} = {
  created: 36000000,
  client_arrived: 3000,
  driver_not_found: 3000,
};

export class OrdersNotificationsStore {
  /**
   * Collection of orders id's that have already been.
   * Needed to determine "event" in fallback receiving order method.
   *
   * @type {{[key: string]: TOrder['id']}}
   */
  @persist('object') @observable orders: { [key: string]: TOrder['id'] } = {};
  /**
   * list of non-opened orders to track
   *
   * @type {Map<TOrder['id'], any>}
   */
  @observable notifications: { [key: string]: TOrderCRUDEvents } = {};

  /**
   * Method should handle new order.
   * Can be called from socket when new order is coming.
   * Can be called from fallback receiving order method.
   *
   * @param {TOrder} order
   * @param {TOrderCRUDEvents} event
   */
  @action onReceiveOrder = (order: TOrder, event: TOrderCRUDEvents) => {
    order = { ...order, id: order.id };
    const { isCallCenterScreen, branchesFilter } = ordersStore;
    // add order as already received order
    this.orders[order.id] = order.id;

    // "Waiting for payment" orders are visible only to those who have permission to "USER_PERMISSIONS.ORDERS_EDIT"
    // Hide "On hold" orders on orders Screen
    const isHiddenStatusOrder: boolean =
      ([ORDER_STATUS.ON_HOLD].includes(order.order_status_code) && !isCallCenterScreen) ||
      ([ORDER_STATUS.WAITING_FOR_PAYMENT, ORDER_STATUS.ON_HOLD].includes(order.order_status_code) && !hasPermissions(USER_PERMISSIONS.ORDERS_EDIT));

    // If some branches selected and incoming order branch not in filter
    const isUnselectedBranchOrder: boolean =
      !isCallCenterScreen && Boolean(branchesFilter.length) && !branchesFilter?.some((branch) => branch.id === order.branch?.id);

    if (isHiddenStatusOrder || isUnselectedBranchOrder) return;

    const orderUpdatedFromInvisible =
      !isCallCenterScreen && event === 'updated' && [ORDER_STATUS.ACCEPTED, ORDER_STATUS.PENDING].includes(order.order_status_code);

    // counters update
    if (
      (['created', 'client_arrived', 'driver_not_found'].includes(event) || orderUpdatedFromInvisible) &&
      (isCallCenterScreen || ordersStore.orderInAdvanceFilter(order))
    ) {
      // appStore.changeNoificationCount(order, true);
    }
    // update order and orders list
    ordersStore.updateOrder(order, { isNew: event === 'created' });
    // update notifications
    this.notificationHandler(order, { event, orderUpdatedFromInvisible });
  };

  @action notificationHandler = (
    order: TOrder,
    { event, orderUpdatedFromInvisible }: { event?: TOrderCRUDEvents; orderUpdatedFromInvisible?: boolean }
  ) => {
    // @TODO: refactor notification
    // Removal of notification when an order is canceled from on_hold status
    if (
      order.dnf?.enable &&
      [ORDER_STATUS.CANCELED].includes(order.order_status_code) &&
      order.previous_status !== null &&
      [ORDER_STATUS.CANCELED, ORDER_STATUS.ON_HOLD].includes(order.previous_status)
    ) {
      delete this.notifications[order.id];
      return null;
    }
    if (!order.viewed && event && ![ORDER_STATUS.WAITING_FOR_PAYMENT, ORDER_STATUS.ON_HOLD].includes(order.order_status_code)) {
      // if order is new or not viewed

      // if order already has another notification we should leave notification with hights duration
      if (this.notifications[order.id]) {
        const _duration = ORDER_NOTIFICATION_TIMEOUT_MAP[this.notifications[order.id]] || 0;
        const duration = ORDER_NOTIFICATION_TIMEOUT_MAP[orderUpdatedFromInvisible ? 'created' : event] || 0;
        // if new duration is biggest we should set new event value
        // in another case leave same value
        if (duration > _duration) {
          this.notifications[order.id] = orderUpdatedFromInvisible ? 'created' : event;
        }
      } else {
        // if there is no order notification
        // add new notification to list
        this.notifications[order.id] = orderUpdatedFromInvisible ? 'created' : event;
      }
    } else {
      // if order is viewed or if select order
      // remove order notifications
      delete this.notifications[order.id];
    }

    // TM-2203
    if (order && event) {
      const orderFromList = ordersStore.pagination.data.find((_order) => _order?.id === order.id);

      // remove notification if order was already received and new order status is changed
      if (orderFromList && orderFromList.order_status_code !== order.order_status_code) {
        delete this.notifications[order.id];
      }
    }

    if (order && ![ORDER_STATUS.WAITING_FOR_PAYMENT, ORDER_STATUS.ON_HOLD].includes(order.order_status_code)) this.onNotificationChanged();
  };

  @action onNotificationChanged = debounce(() => {
    try {
      // if user does not have permissions for orders management we should not play sound
      if (!hasPermissions(USER_PERMISSIONS.ORDERS_MANAGMENT)) return;

      let timeout = 0;
      // find longest duration of notification
      for (const notification of Object.values(this.notifications)) {
        const duration = ORDER_NOTIFICATION_TIMEOUT_MAP[notification] || 0;
        if (duration > timeout) timeout = duration;
      }
      // play sound notification or stop it
      if (timeout) {
        player.play(timeout);
      } else {
        player.stop();
      }
    } catch (error: any) {
      logger.error({ error, place: 'OrdersStore onNotificationChanged' });
    }
  }, 500);

  @action clearNotifications = () => {
    this.notifications = {};
    player.stop();
  };

  constructor() {
    makeObservable(this);
  }
}
